import React, { useMemo } from 'react';
import { Flex, IconButton, Text } from '@vibe/core';
import { Add, Delete, Edit } from '@vibe/icons';
import { format, parseISO, eachDayOfInterval, isWeekend } from 'date-fns';
import type { WorklogRow } from './TimeTrackTable';
import { useUsersStore } from '../../stores/usersStore';
import { formatHoursMinutes } from '../../utils/timesheet-capacity';

// ─── Props ─────────────────────────────────────────────────────────────────────

export interface TimeTrackStackProps {
  /** Array of worklog entries to display */
  worklogs: WorklogRow[];
  /** Callback when the user clicks the edit action */
  onEdit: (id: string) => void;
  /** Callback when the user clicks the delete action */
  onDelete: (id: string) => void;
  /** Start date of the visible range (yyyy-MM-dd) */
  dateRangeStart?: string;
  /** End date of the visible range (yyyy-MM-dd) */
  dateRangeEnd?: string;
  /** Callback to add a worklog for a specific day */
  onAddForDay?: (date: string) => void;
  /** When true, hides all edit/delete/add controls (period is locked) */
  readOnly?: boolean;
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

interface DayGroup {
  /** ISO date key (yyyy-MM-dd) */
  dateKey: string;
  /** Day name (e.g. "Mon") */
  dayName: string;
  /** Date label (e.g. "Oct 16") */
  dateLabel: string;
  /** Total hours for the day */
  totalHours: number;
  /** Whether this is a weekend day */
  isWeekend: boolean;
  /** Worklogs belonging to this day */
  entries: WorklogRow[];
}

function buildDayGroups(
  worklogs: WorklogRow[],
  dateRangeStart?: string,
  dateRangeEnd?: string,
): DayGroup[] {
  // Build a map of worklogs per day
  const map = new Map<string, WorklogRow[]>();

  for (const w of worklogs) {
    const key = w.date.slice(0, 10);
    const existing = map.get(key);
    if (existing) {
      existing.push(w);
    } else {
      map.set(key, [w]);
    }
  }

  // If date range is provided, generate columns for all days in the range
  let dayKeys: string[];
  if (dateRangeStart && dateRangeEnd) {
    const start = parseISO(dateRangeStart);
    const end = parseISO(dateRangeEnd);
    if (start <= end) {
      dayKeys = eachDayOfInterval({ start, end }).map((d) =>
        format(d, 'yyyy-MM-dd'),
      );
    } else {
      dayKeys = Array.from(map.keys()).sort();
    }
  } else {
    // Fallback: show only days that have worklogs, sorted ascending
    dayKeys = Array.from(map.keys()).sort();
  }

  return dayKeys.map((dateKey) => {
    const entries = map.get(dateKey) ?? [];
    const dt = parseISO(dateKey);
    const totalHours = entries.reduce(
      (sum, e) => sum + (Number(e.hours) || 0),
      0,
    );
    return {
      dateKey,
      dayName: format(dt, 'EEE'),
      dateLabel: format(dt, 'MMM d'),
      totalHours,
      isWeekend: isWeekend(dt),
      entries,
    };
  });
}


// ─── Component ──────────────────────────────────────────────────────────────

/**
 * Stack (kanban-like) view for the Time Tracking workspace.
 *
 * Groups worklogs by day into vertical columns. Each column shows:
 * - Day name + date
 * - Worklog count + total hours badge
 * - "+" button to add a new worklog
 * - Cards for each worklog entry with user avatar, name, hours, item
 *
 * Reference: TeamBoard documentation pages 78-79 showing the Stack mode.
 */
const TimeTrackStack: React.FC<TimeTrackStackProps> = ({
  worklogs,
  onEdit,
  onDelete,
  dateRangeStart,
  dateRangeEnd,
  onAddForDay,
  readOnly = false,
}) => {
  const users = useUsersStore((s) => s.users);
  const dayGroups = useMemo(
    () => buildDayGroups(worklogs, dateRangeStart, dateRangeEnd),
    [worklogs, dateRangeStart, dateRangeEnd],
  );

  return (
    <div className="rp-tt-stack">
      <div className="rp-tt-stack__columns">
        {dayGroups.map((group) => (
          <div
            key={group.dateKey}
            className={`rp-tt-stack__column ${
              group.isWeekend ? 'rp-tt-stack__column--weekend' : ''
            }`}
          >
            {/* Column header */}
            <div className="rp-tt-stack__column-header">
              <div className="rp-tt-stack__column-date">
                <span className="rp-tt-stack__day-name">{group.dayName}</span>
                <span className="rp-tt-stack__date-label">
                  {group.dateLabel}
                </span>
              </div>
              <div className="rp-tt-stack__column-meta">
                <span className="rp-tt-stack__worklog-count">
                  {group.entries.length}{' '}
                  {group.entries.length === 1 ? 'worklog' : 'worklogs'}
                </span>
                {group.totalHours > 0 && (
                  <span className="rp-tt-stack__hours-badge">
                    {formatHoursMinutes(group.totalHours)}
                  </span>
                )}
                {onAddForDay && !readOnly && (
                  <button
                    className="rp-tt-stack__add-btn"
                    onClick={() => onAddForDay(group.dateKey)}
                    title="Add worklog"
                  >
                    +
                  </button>
                )}
              </div>
            </div>

            {/* Cards */}
            <div className="rp-tt-stack__cards">
              {group.entries.map((entry) => (
                <div key={entry.id} className="rp-tt-stack__card">
                  <div className="rp-tt-stack__card-body">
                    {/* User row */}
                    <div className="rp-tt-stack__card-user-row">
                      <span className="rp-tt-stack__card-avatar">
                        {(entry.user_name ?? 'U').charAt(0).toUpperCase()}
                      </span>
                      <Text
                        type={Text.types.TEXT2}
                        ellipsis
                        style={{ flex: 1 }}
                      >
                        {entry.user_name || `User #${entry.monday_user_id}`}
                      </Text>
                      <span className="rp-tt-stack__card-hours-badge">
                        {formatHoursMinutes(entry.hours)}
                      </span>
                    </div>

                    {/* Item name */}
                    <Text
                      type={Text.types.TEXT2}
                      weight={Text.weights.MEDIUM}
                      ellipsis
                      className="rp-tt-stack__card-title"
                      tooltipProps={{
                        content:
                          entry.item_name ??
                          `Item #${entry.monday_item_id}`,
                      }}
                    >
                      {entry.item_name ??
                        `Item #${entry.monday_item_id}`}
                    </Text>
                    {/* Board > Group context */}
                    {(entry.board_name || entry.group_name) && (
                      <Text
                        type={Text.types.TEXT2}
                        ellipsis
                        style={{ color: '#676879', fontSize: '11px', marginTop: '1px' }}
                      >
                        {[entry.board_name, entry.group_name].filter(Boolean).join(' > ')}
                      </Text>
                    )}

                    {/* Description */}
                    {entry.description && (
                      <Text
                        type={Text.types.TEXT2}
                        style={{ color: '#676879', marginTop: '2px' }}
                        ellipsis
                        tooltipProps={{ content: entry.description }}
                      >
                        {entry.description}
                      </Text>
                    )}

                    {/* Created by (when different from assignee) */}
                    {entry.createdByUserId && entry.createdByUserId !== entry.monday_user_id && (
                      <Text
                        type={Text.types.TEXT2}
                        style={{ color: '#676879', fontSize: '11px', marginTop: '2px' }}
                      >
                        Created by: {users.find((u) => String(u.id) === entry.createdByUserId)?.name ?? `User #${entry.createdByUserId}`}
                      </Text>
                    )}
                  </div>

                  {/* Card actions */}
                  {!readOnly && (
                    <div className="rp-tt-stack__card-actions">
                      <IconButton
                        icon={Edit}
                        size={IconButton.sizes.XXS}
                        onClick={() => onEdit(entry.id)}
                        ariaLabel="Edit worklog"
                      />
                      <IconButton
                        icon={Delete}
                        size={IconButton.sizes.XXS}
                        onClick={() => onDelete(entry.id)}
                        ariaLabel="Delete worklog"
                      />
                    </div>
                  )}
                </div>
              ))}

              {/* Add button at bottom of column */}
              {onAddForDay && !readOnly && group.entries.length > 0 && (
                <button
                  className="rp-tt-stack__add-card-btn"
                  onClick={() => onAddForDay(group.dateKey)}
                >
                  +
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TimeTrackStack;
